import React, { useState, type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import sortBy from 'lodash/sortBy';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import PersonIcon from '@mui/icons-material/Person';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import { Button } from 'components/Shared/Inputs';
import { KebabMenu } from 'components/Shared/KebabMenu';
import { ActivityConsentFormUploader } from './ActivityConsentFormUploader';
import { ActivityConsentFormUploaderDialog } from './ActivityConsentFormUploaderDialog';
import { useCardLoading } from 'components/Shared';
import useTimezoneUtils from 'hooks/useTimezoneUtils';
import { ALLOWED_DOCUMENT_TYPES } from './type';
import { notify } from 'components/Shared/NotifyDialog';
import { useDeleteFile, useSearchFiles } from 'api/queries/fileUploadQuery';
import { type DocumentDescriptor } from 'components/ProviderProfile/interfaces';
import { useAppConfig } from 'providers';
import { snackBarMessageWithSupportId } from '@/TelemetryInit';

import './ActivityConsentFormList.scss';

interface ActivityConsentFormListProps {
  activityId: number;
  isReadOnly: boolean;
}

export const ActivityConsentFormList: FC<ActivityConsentFormListProps> = ({ activityId, isReadOnly }) => {
  const { formatDate, formatToProviderTime: formatTime } = useTimezoneUtils();
  const { t } = useTranslation(['documentUpload', 'common'], { useSuspense: false });
  const [displayActivityConsentFormUploader, setDisplayActivityConsentFormUploader] = useState(false);
  const { setLoading } = useCardLoading();
  const { REACT_APP_FILEUPLOAD_HOST } = useAppConfig();
  const baseUrl = `${REACT_APP_FILEUPLOAD_HOST}/v1`;
  const { data, isLoading } = useSearchFiles({ Type: 'activity', Id: activityId });
  const MAX_DOCUMENT_PER_ACTIVITY = 20;

  const { mutateAsync: deleteFile } = useDeleteFile({
    onSuccess: (_, variables) => handleSuccess(variables),
    onError: (error) => handleError(error)
  });

  const { enqueueSnackbar } = useSnackbar();
  const [documents, setDocuments] = useState<DocumentDescriptor[]>([]);

  setLoading(isLoading);

  useEffect(() => {
    if (data) {
      const filteredDocuments = data
        .filter(
          (doc) => doc.metadata?.document_type && ALLOWED_DOCUMENT_TYPES.includes(doc.metadata.document_type)
        );
      setDocuments(getSortedDocument(filteredDocuments as DocumentDescriptor[]));
    }
  }, [data]);

  const handleActivityConsentFormUploaderClose = (data: DocumentDescriptor | undefined) => {
    if (data) {
      setDocuments(getSortedDocument([...documents, data]));
    }
    setDisplayActivityConsentFormUploader(false);
  };

  function handleSuccess(id?: string): void {
    const deletedDoc = documents.findLast((doc) => doc.fileId === id);
    enqueueSnackbar(`${t(deletedDoc?.metadata?.document_type)} ${t('deleted')}.`, { variant: 'success' });
    setDocuments([...documents.filter((doc) => doc.fileId !== id)]);
  }

  function handleError(error): void {
    enqueueSnackbar(snackBarMessageWithSupportId(`${t('"file_delete_failed"')}: ${error.message}`), { variant: 'error' });
  }

  const handleDelete = async (id: string) => {
    await deleteFile(id);
  };

  const getSortedDocument = (docs: DocumentDescriptor[]) => {
    return sortBy(docs, (doc) => doc.metadata?.document_type);
  };

  const getFormattedTimestamp = (datestr) => {
    const date = moment.utc(datestr).local();
    return `${formatDate(date, 'ddd ll')} | ${formatTime(date)}`;
  };

  const getDownloadUrl = (id: string) => {
    return id ? `${baseUrl}/file/${id}/download` : '#';
  };

  const reachedMaxDocument = (documents: DocumentDescriptor[]) => {
    return documents.length >=  MAX_DOCUMENT_PER_ACTIVITY;
  } 

  const downloadFile = (doc: DocumentDescriptor) => {
    const url = getDownloadUrl(doc.fileId!);
    window.open(url, '_blank', 'noreferrer');
  };

  const getPreviewUrl = (id: string) => {
      return id ? `${baseUrl}/file/${id}/preview` : '#';
  };

  const displayNote = async (notes: string) => {
    await notify({
      title: t('common:note'),
      message: atob(notes)
    });
  };

 
const decodeBase64 = (text: string | undefined) => {
  if(!text) return '';

  const isBase64 = (str) => {
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    
    if (!base64Regex.test(str) || str.length % 4 !== 0 ) {
      return false;
    }
    
    try {
      const decoded = atob(str);
      const printableChars = decoded.split('').filter(char => {
        const code = char.charCodeAt(0);
        return code >= 32 && code <= 126; // Printable ASCII range
      });
      
      return printableChars.length / decoded.length >= 0.8;
    } catch {
      return false;
    }
  };

  if (isBase64(text)) {
    try {
      return atob(text);
    } catch {
      return text;
    }
  }
  
  return text;
};

  return (
    <div className="document-list-container">
      <div className="document-item-header">
        <div>
          <h2>{t('documents')}</h2>
        </div>
        <div className="document-meta">
          <Button variant="outline"
                  disabled={isReadOnly || reachedMaxDocument(documents)}
                  onClick={() => setDisplayActivityConsentFormUploader(true)}>
            {t('common:upload')}
          </Button>

        </div>
      </div>
      {
        reachedMaxDocument(documents) && (
          <Alert severity="warning" className='upload-limit-alert'>
            {t('files_uploaded_reach_limit')}
          </Alert>
        )
      }
        {documents.length > 0 ? (
        <div className="document-items-wrapper">
          {documents.map((doc) => (
            <div key={doc.fileId} className="document-item">
              <div className="document-info">
                <div className="document-title">
                  <Tooltip title={t(doc?.metadata?.document_type ?? '')}  placement="top" arrow>
                    <a href={getPreviewUrl(doc.fileId!)} target='_blank' rel='noreferrer'>{t(doc?.metadata?.document_type ?? '')}</a>
                  </Tooltip>
                  <KebabMenu
                    getMenuItems={() => [
                      {
                        label: `${t('common:download')}`,
                        icon: <DownloadIcon fontSize="small" />,
                        className: 'kebabmenu-item',
                        onClick: () => downloadFile(doc),
                      },
                      {
                        label: `${t('view_note')}`,
                        icon: <NoteAltIcon fontSize="small" />,
                        className: 'kebabmenu-item',
                        onClick: () => displayNote(doc?.metadata?.notes),
                        hidden: !doc?.metadata?.notes
                      },
                      {
                        label: `${t('common:delete')}`,
                        icon: <DeleteIcon fontSize="small" />,
                        className: 'kebabmenu-item',
                        onClick: () => handleDelete(doc.fileId!),
                        hidden: isReadOnly,
                      },
                    ]}
                  />
                </div>
                <div className="document-meta">
                  <div className="label">
                    <DescriptionIcon fontSize="small"/>
                    {doc.fileName}
                  </div>
                </div>
                <div className="document-meta">
                  <div className='label'>
                    <PersonIcon fontSize="small"/>
                    {`${decodeBase64(doc.metadata?.providerFirstName)} ${decodeBase64(doc.metadata?.providerLastName)}`}
                  </div>
                  {doc.metadata?.notes && (
                    <div className='label note' onClick={()=> displayNote(doc?.metadata?.notes)}>
                      {' • '}
                      <NoteAltIcon fontSize="small" />
                      {t('common:note')}
                    </div>
                  )}
                </div>
                <div className="document-meta date">{getFormattedTimestamp(doc.createdOn)}</div>
              </div>
            </div>))}
        </div>
        ) : (
          <div className="no-documents-container"  >
            <div><DescriptionIcon fontSize="large" className='icon' /></div>
            <div>{t('nodocument')}</div>
            <Button 
              variant="outline" 
              onClick={() => setDisplayActivityConsentFormUploader(true)}
              disabled={isReadOnly || reachedMaxDocument(documents)}
            >
              {t('common:upload')}              
            </Button>
          </div>
        )}

      <ActivityConsentFormUploaderDialog
        open={displayActivityConsentFormUploader}
        content={
          <ActivityConsentFormUploader activityId={activityId} onClose={handleActivityConsentFormUploaderClose} />
        }
        onClose={() => setDisplayActivityConsentFormUploader(false)}
      />
    </div>
  );
};